/* eslint-disable */

// src/components/LoggerTester.js
import React, { useState, useEffect } from 'react';

const API_BASE_URL = 'https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1';

const LoggerTester = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [actionId, setActionId] = useState(null);
  const [correlationId, setCorrelationId] = useState(null);
  const [activeTab, setActiveTab] = useState('tester'); // 'tester' or 'dashboard'
  
  // KPI Dashboard State
  const [kpiData, setKpiData] = useState({
    totalActions: 0,
    totalErrors: 0,
    totalApiCalls: 0,
    avgPerformance: 0,
    successRate: 0,
    recentActions: [],
    performanceData: [],
    productStats: {
      virtual: 0,
      physical: 0,
      buy: 0,
      sell: 0,
      shipping: 0
    }
  });

  // Helper function to get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('accessToken');
  };

  // Add log message
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [{ timestamp, message, type }, ...prev]);
  };

  // Fetch KPI Data from Database
  const fetchKpiData = async () => {
    try {
      // Mock data - In real scenario, fetch from your backend APIs
      // You'll need to create these endpoints in your backend
      
      const mockData = {
        totalActions: 1247,
        totalErrors: 23,
        totalApiCalls: 8956,
        avgPerformance: 245,
        successRate: 98.2,
        recentActions: [
          { id: 1, action: 'Product Export', status: 'SUCCESS', time: '2024-01-15 10:30:00', duration: 234 },
          { id: 2, action: 'Bulk Import', status: 'SUCCESS', time: '2024-01-15 09:45:00', duration: 5678 },
          { id: 3, action: 'Shopify Sync', status: 'ERROR', time: '2024-01-15 09:15:00', duration: 1234 },
          { id: 4, action: 'Order Processing', status: 'SUCCESS', time: '2024-01-15 08:30:00', duration: 456 },
          { id: 5, action: 'Price Update', status: 'SUCCESS', time: '2024-01-15 08:00:00', duration: 789 }
        ],
        performanceData: [
          { hour: '00:00', avg: 120 },
          { hour: '04:00', avg: 100 },
          { hour: '08:00', avg: 150 },
          { hour: '12:00', avg: 280 },
          { hour: '16:00', avg: 320 },
          { hour: '20:00', avg: 200 }
        ],
        productStats: {
          virtual: 342,
          physical: 891,
          buy: 567,
          sell: 1234,
          shipping: 456
        }
      };
      
      setKpiData(mockData);
      addLog('📊 KPI Dashboard data refreshed', 'success');
    } catch (error) {
      console.error('Error fetching KPI data:', error);
      addLog('❌ Failed to fetch KPI data', 'error');
    }
  };

  // Auto-refresh KPI data every 30 seconds when on dashboard tab
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchKpiData();
      const interval = setInterval(fetchKpiData, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // =============================================
  // LOGGER TESTER FUNCTIONS (Existing)
  // =============================================
  const handleStartAction = async () => {
    setLoading(true);
    try {
      const newCorrelationId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setCorrelationId(newCorrelationId);

      const response = await fetch(`${API_BASE_URL}/action/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
        },
        body: JSON.stringify({
          company_id: 1,
          user_id: 1,
          action_type: 'TEST_ACTION',
          action_name: 'Frontend Test Action',
          module: 'TESTING',
          sub_module: 'LoggerTest',
          entity_type: 'test',
          source: 'FRONTEND',
          total_count: 100,
          correlation_id: newCorrelationId,
          metadata: { test: true, timestamp: new Date().toISOString() }
        })
      });

      const data = await response.json();
      if (data.success) {
        setActionId(data.action_id);
        addLog(`✅ Action Started - ID: ${data.action_id}`, 'success');
        // Refresh KPI after new action
        if (activeTab === 'dashboard') fetchKpiData();
      } else {
        addLog(`❌ Failed to start action: ${JSON.stringify(data)}`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const handleUpdateProgress = async () => {
    if (!actionId) {
      addLog('⚠️ Please start an action first!', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/action/${actionId}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
        },
        body: JSON.stringify({
          current_step: 'Processing data...',
          progress: 50,
          success_count: 25,
          failure_count: 2,
          status: 'IN_PROGRESS'
        })
      });

      const data = await response.json();
      if (data.success) {
        addLog(`🟡 Progress Updated - 50% complete`, 'info');
      } else {
        addLog(`❌ Update failed`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const handleCompleteAction = async () => {
    if (!actionId) {
      addLog('⚠️ Please start an action first!', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/action/${actionId}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
        },
        body: JSON.stringify({
          status: 'SUCCESS',
          success_count: 98,
          failure_count: 2,
          error_summary: '2 records failed validation'
        })
      });

      const data = await response.json();
      if (data.success) {
        addLog(`🟢 Action Completed Successfully!`, 'success');
        setActionId(null);
        if (activeTab === 'dashboard') fetchKpiData();
      } else {
        addLog(`❌ Complete failed`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const handleLogError = async () => {
    if (!actionId) {
      addLog('⚠️ Please start an action first!', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
        },
        body: JSON.stringify({
          action_id: actionId,
          company_id: 1,
          user_id: 1,
          entity_type: 'test',
          step: 'VALIDATION',
          error_type: 'VALIDATION_ERROR',
          error_message: 'Test error message from frontend',
          severity: 'MEDIUM',
          is_retryable: 0,
          correlation_id: correlationId
        })
      });

      const data = await response.json();
      if (data.success) {
        addLog(`🔴 Error Logged Successfully`, 'error');
        if (activeTab === 'dashboard') fetchKpiData();
      } else {
        addLog(`❌ Error logging failed`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const handleLogApiCall = async () => {
    if (!actionId) {
      addLog('⚠️ Please start an action first!', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
        },
        body: JSON.stringify({
          action_id: actionId,
          company_id: 1,
          service_name: 'test_service',
          external_service: 'External API',
          endpoint: '/test/endpoint',
          method: 'GET',
          request: { test: true },
          response: { status: 'ok' },
          status_code: 200,
          duration_ms: 150,
          step: 'API_CALL',
          correlation_id: correlationId
        })
      });

      const data = await response.json();
      if (data.success) {
        addLog(`🔵 API Call Logged - Duration: 150ms`, 'info');
        if (activeTab === 'dashboard') fetchKpiData();
      } else {
        addLog(`❌ API log failed`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const handleLogPerformance = async () => {
    if (!actionId) {
      addLog('⚠️ Please start an action first!', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/performance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
        },
        body: JSON.stringify({
          action_id: actionId,
          service_name: 'data_processor',
          operation: 'processBatch',
          duration_ms: 1250,
          status: 'SUCCESS',
          step: 'PROCESSING',
          metadata: { records: 500, speed: 'fast' },
          correlation_id: correlationId
        })
      });

      const data = await response.json();
      if (data.success) {
        addLog(`🟣 Performance Logged - Duration: 1250ms`, 'info');
      } else {
        addLog(`❌ Performance log failed`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const handleLogAiCall = async () => {
    if (!actionId) {
      addLog('⚠️ Please start an action first!', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
        },
        body: JSON.stringify({
          action_id: actionId,
          company_id: 1,
          ai_service: 'openai',
          input_quality: 'high',
          success: 1,
          confidence_score: 0.95,
          input: { prompt: 'Test prompt' },
          output: { result: 'Test output' },
          step: 'AI_PROCESSING',
          model_version: 'gpt-4',
          correlation_id: correlationId
        })
      });

      const data = await response.json();
      if (data.success) {
        addLog(`🤖 AI Call Logged - Confidence: 95%`, 'info');
      } else {
        addLog(`❌ AI log failed`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const handleLogJob = async () => {
    if (!actionId) {
      addLog('⚠️ Please start an action first!', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/job`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
        },
        body: JSON.stringify({
          action_id: actionId,
          job_type: 'background_processing',
          status: 'PROCESSING',
          retry_count: 1,
          max_retries: 3,
          step: 'JOB_EXECUTION',
          metadata: { priority: 'high' },
          correlation_id: correlationId
        })
      });

      const data = await response.json();
      if (data.success) {
        addLog(`🟠 Job Logged - Type: background_processing`, 'info');
      } else {
        addLog(`❌ Job log failed`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const handleLogSystem = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/system`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
        },
        body: JSON.stringify({
          level: 'INFO',
          message: 'System test log from frontend',
          service_name: 'frontend-app',
          context: { user: 'test', action: 'logging_test' }
        })
      });

      const data = await response.json();
      if (data.success) {
        addLog(`⚫ System Logged - Level: INFO`, 'info');
      } else {
        addLog(`❌ System log failed`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const clearLogs = () => {
    setLogs([]);
    addLog('🗑️ Logs cleared', 'info');
  };

  // =============================================
  // KPI DASHBOARD RENDER
  // =============================================
  const renderDashboard = () => (
    <div>
      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div style={cardStyle('#2196f3')}>
          <div style={{ fontSize: '40px' }}>🎯</div>
          <div>
            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{kpiData.totalActions.toLocaleString()}</div>
            <div style={{ color: '#666' }}>Total Actions</div>
          </div>
        </div>

        <div style={cardStyle('#f44336')}>
          <div style={{ fontSize: '40px' }}>⚠️</div>
          <div>
            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{kpiData.totalErrors}</div>
            <div style={{ color: '#666' }}>Total Errors</div>
          </div>
        </div>

        <div style={cardStyle('#4caf50')}>
          <div style={{ fontSize: '40px' }}>⚡</div>
          <div>
            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{kpiData.avgPerformance}ms</div>
            <div style={{ color: '#666' }}>Avg Performance</div>
          </div>
        </div>

        <div style={cardStyle('#ff9800')}>
          <div style={{ fontSize: '40px' }}>📈</div>
          <div>
            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{kpiData.successRate}%</div>
            <div style={{ color: '#666' }}>Success Rate</div>
          </div>
        </div>
      </div>

      {/* Product Stats */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0 }}>📦 Product Statistics</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '15px'
        }}>
          <div style={statBoxStyle('#e3f2fd', '#2196f3')}>
            <div>💻 Virtual</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{kpiData.productStats.virtual}</div>
          </div>
          <div style={statBoxStyle('#fff3e0', '#ff9800')}>
            <div>📦 Physical</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{kpiData.productStats.physical}</div>
          </div>
          <div style={statBoxStyle('#e8f5e9', '#4caf50')}>
            <div>🛒 Buy</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{kpiData.productStats.buy}</div>
          </div>
          <div style={statBoxStyle('#fce4ec', '#f44336')}>
            <div>💰 Sell</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{kpiData.productStats.sell}</div>
          </div>
          <div style={statBoxStyle('#f3e5f5', '#9c27b0')}>
            <div>🚚 Shipping</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{kpiData.productStats.shipping}</div>
          </div>
        </div>
      </div>

      {/* Recent Actions Table */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0 }}>📋 Recent Actions</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.recentActions.map((action) => (
                <tr key={action.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px' }}>{action.id}</td>
                  <td style={{ padding: '12px' }}>{action.action}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: action.status === 'SUCCESS' ? '#4caf50' : 
                                 action.status === 'ERROR' ? '#f44336' : '#ff9800',
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {action.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>{action.time}</td>
                  <td style={{ padding: '12px' }}>{action.duration}ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // =============================================
  // TESTER RENDER (Existing)
  // =============================================
  const renderTester = () => (
    <>
      {/* Status Bar */}
      <div style={{ 
        background: '#f0f0f0', 
        padding: '10px', 
        borderRadius: '5px', 
        marginBottom: '20px',
        display: 'flex',
        gap: '20px'
      }}>
        <div><strong>Action ID:</strong> {actionId || 'None'}</div>
        <div><strong>Correlation ID:</strong> {correlationId || 'None'}</div>
      </div>

      {/* Buttons Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
        gap: '10px',
        marginBottom: '20px'
      }}>
        <button onClick={handleStartAction} disabled={loading} style={buttonStyle('#4CAF50')}>
          🟢 1. Start Action
        </button>
        
        <button onClick={handleUpdateProgress} disabled={loading || !actionId} style={buttonStyle('#FFC107')}>
          🟡 2. Update Progress
        </button>
        
        <button onClick={handleCompleteAction} disabled={loading || !actionId} style={buttonStyle('#2196F3')}>
          🔵 3. Complete Action
        </button>
        
        <button onClick={handleLogError} disabled={loading || !actionId} style={buttonStyle('#f44336')}>
          🔴 4. Log Error
        </button>
        
        <button onClick={handleLogApiCall} disabled={loading || !actionId} style={buttonStyle('#9C27B0')}>
          📡 5. Log API Call
        </button>
        
        <button onClick={handleLogPerformance} disabled={loading || !actionId} style={buttonStyle('#FF9800')}>
          ⚡ 6. Log Performance
        </button>
        
        <button onClick={handleLogAiCall} disabled={loading || !actionId} style={buttonStyle('#673AB7')}>
          🤖 7. Log AI Call
        </button>
        
        <button onClick={handleLogJob} disabled={loading || !actionId} style={buttonStyle('#795548')}>
          📦 8. Log Job
        </button>
        
        <button onClick={handleLogSystem} disabled={loading} style={buttonStyle('#607D8B')}>
          💻 9. Log System
        </button>
        
        <button onClick={clearLogs} style={buttonStyle('#cccccc', '#000')}>
          🗑️ Clear Logs
        </button>
      </div>

      {/* Logs Display */}
      <div style={{ 
        border: '1px solid #ddd', 
        borderRadius: '5px', 
        padding: '10px',
        background: '#fafafa',
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        <h3>📝 Log Output:</h3>
        {logs.length === 0 ? (
          <p style={{ color: '#999' }}>No logs yet. Click any button to start...</p>
        ) : (
          logs.map((log, index) => (
            <div key={index} style={{ 
              padding: '5px', 
              borderBottom: '1px solid #eee',
              fontFamily: 'monospace',
              fontSize: '12px',
              color: log.type === 'error' ? '#f44336' : log.type === 'success' ? '#4CAF50' : log.type === 'warning' ? '#FF9800' : '#333'
            }}>
              [{log.timestamp}] {log.message}
            </div>
          ))
        )}
      </div>
    </>
  );

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '20px' }}>📊 Logger & KPI Dashboard</h1>
      
      {/* Tab Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '20px',
        borderBottom: '2px solid #ddd',
        paddingBottom: '10px'
      }}>
        <button
          onClick={() => setActiveTab('tester')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'tester' ? '#2196F3' : '#f0f0f0',
            color: activeTab === 'tester' ? 'white' : '#333',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          🔧 API Logger Tester
        </button>
        <button
          onClick={() => setActiveTab('dashboard')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'dashboard' ? '#2196F3' : '#f0f0f0',
            color: activeTab === 'dashboard' ? 'white' : '#333',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          📊 KPI Dashboard
        </button>
      </div>

      {/* Render Active Tab */}
      {activeTab === 'tester' ? renderTester() : renderDashboard()}

      {/* Common Instructions */}
      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        background: '#e3f2fd', 
        borderRadius: '5px',
        fontSize: '14px'
      }}>
        <h4>📖 Instructions:</h4>
        <ol>
          <li><strong>Logger Tester:</strong> APIs test karne ke liye</li>
          <li><strong>KPI Dashboard:</strong> Real-time metrics dekhne ke liye</li>
          <li>Dashboard auto-refresh hota hai every 30 seconds</li>
        </ol>
        <p><strong>Note:</strong> Token localStorage mein 'token' ya 'accessToken' key se store hona chahiye</p>
      </div>
    </div>
  );
};

// Style helpers
const cardStyle = (color) => ({
  background: 'white',
  borderRadius: '10px',
  padding: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  borderLeft: `4px solid ${color}`
});

const statBoxStyle = (bgColor, color) => ({
  background: bgColor,
  padding: '15px',
  borderRadius: '8px',
  textAlign: 'center',
  color: color
});

const buttonStyle = (bgColor, textColor = 'white') => ({
  background: bgColor,
  color: textColor,
  padding: '10px',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontWeight: 'bold'
});

export default LoggerTester;